import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { Observable, switchMap, catchError, throwError } from "rxjs";
import {environment} from "../../environments/environment";
import {protectedResourceMap} from "../app.module";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRedirecting = false;
  private redirectAttempts = 0;
  private lastRedirectTime = 0;

  constructor(private msalService: MsalService ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const activeAccount = this.msalService.instance.getActiveAccount()
    
    if (activeAccount) {
      const scopes = this.getScopes(req.url)
      
      if (scopes.length === 0) {
        return next.handle(req);
      }
      
      return this.msalService.acquireTokenSilent({
        account: activeAccount, scopes: scopes
      })
        .pipe(
          switchMap((authRequest) => {
            const token = authRequest.accessToken
            const clonedReq = req.clone({
              headers: req.headers.set("Authorization",`Bearer ${token}`)
            })
            return next.handle(clonedReq).pipe(
              catchError((error: HttpErrorResponse) => {
                if (error.status === 401 && !this.isRedirecting) {
                  console.error('API returned 401 - token invalid, redirecting to login');
                  this.isRedirecting = true;
                  this.msalService.loginRedirect({ scopes });
                }
                return throwError(() => error);
              })
            )
          }),
          catchError((error) => {
            // Check if this is a network/server error vs auth error
            const isNetworkError = 
              error?.errorCode === 'network_error' ||
              error?.errorCode === 'service_unavailable' ||
              error?.message?.includes('timeout') ||
              error?.message?.includes('Network request failed');
            
            // If network error, don't redirect - just log and fail the request
            if (isNetworkError) {
              console.error('Network error during token acquisition:', error?.message);
              return throwError(() => error);
            }
            
            // Check for various token failure scenarios that require interaction
            const requiresInteraction = 
              error instanceof InteractionRequiredAuthError ||
              error?.errorCode === 'interaction_required' ||
              error?.errorCode === 'login_required' ||
              error?.errorCode === 'consent_required' ||
              error?.name === 'InteractionRequiredAuthError';
            
            if (requiresInteraction && !this.isRedirecting) {
              const now = Date.now();
              const timeSinceLastRedirect = now - this.lastRedirectTime;
              const redirectCooldownMs = 60000; // 1 minute
              const maxRedirectAttempts = 3;
              
              // Check redirect cooldown
              if (timeSinceLastRedirect < redirectCooldownMs) {
                console.warn('Redirect cooldown active - skipping redirect to prevent loop');
                return throwError(() => error);
              }
              
              // Check attempt limit
              if (this.redirectAttempts >= maxRedirectAttempts) {
                console.error('Max redirect attempts reached - stopping to prevent infinite loop');
                // Reset after 5 minutes
                setTimeout(() => {
                  this.redirectAttempts = 0;
                  this.isRedirecting = false;
                }, 5 * 60 * 1000);
                return throwError(() => error);
              }
              
              console.error('Token expired - redirecting to login (attempt ' + (this.redirectAttempts + 1) + ')');
              this.isRedirecting = true;
              this.redirectAttempts++;
              this.lastRedirectTime = now;
              
              this.msalService.loginRedirect({ scopes }).subscribe({
                next: () => {
                  this.redirectAttempts = 0;
                  this.isRedirecting = false;
                },
                error: (redirectError) => {
                  console.error('Login redirect failed:', redirectError);
                  this.isRedirecting = false;
                }
              });
            } else if (error) {
              console.error('Token acquisition failed:', error?.errorMessage || error?.message);
            }
            return throwError(() => error);
          })
        )
    }
    return next.handle(req)
  }


  getScopes(reqUrl: string) : string[] {
    if (reqUrl.includes(environment.messagesBaseUrl) || reqUrl.includes(environment.contactApiBaseUrl)) {
      const [splitUrl] = reqUrl.split("/api/")
      return protectedResourceMap.get(splitUrl) || []
    } else if (reqUrl.includes(environment.trainOrderBaseUrl)) {
      // For proxy URLs that start with /api/, use the base URL directly as the key
      if (environment.trainOrderBaseUrl.startsWith('/api/')) {
        return protectedResourceMap.get(environment.trainOrderBaseUrl) || []
      }
      // For full URLs, extract the base URL
      const [splitUrl] = reqUrl.split("/api/")
      return protectedResourceMap.get(splitUrl) || []
    } else if (reqUrl.includes(environment.alcolockApiBaseUrl)) {
      // Handle both full URL and proxy URL patterns
      if (environment.alcolockApiBaseUrl.startsWith('/api/')) {
        // For local proxy URLs, we need to find the matching key in the map
        const mapKey = Array.from(protectedResourceMap.keys()).find(key => 
          !key.startsWith('http') && key.includes('alcolock')
        );
        if (mapKey) {
          return protectedResourceMap.get(mapKey) || [];
        }
      }
      // For full URLs, extract the base URL
      const [splitUrl] = reqUrl.split("/api/")
      return protectedResourceMap.get(splitUrl) || []
    } else if (reqUrl.includes(environment.graphUrl)) {
      const [splitUrl] = reqUrl.split("v1.0")
      return protectedResourceMap.get(splitUrl + "v1.0") || []
    } else {
      return [];
    }
  }
}
